import ar from "./catalogs/ar.json";
import en from "./catalogs/en.json";
import type { Locale } from "./locale";

/** A nested, namespaced message catalog (`{ dashboard: { title: "…" } }`). */
export interface Catalog {
  readonly [segment: string]: string | Catalog;
}

type Leaves<T, Prefix extends string = ""> = {
  [K in keyof T & string]: T[K] extends string ? `${Prefix}${K}` : Leaves<T[K], `${Prefix}${K}.`>;
}[keyof T & string];

/**
 * Every valid message key, derived from the English catalog (the reference catalog).
 * Namespaces: `common.*`, `ui.*` (shared components), one per app (`dashboard.*`,
 * `admin.*`, `pages.*`), `storybook.*` (demo copy for stories), `auth.*` (API auth errors),
 * `workspace.*` (API workspace errors), `billing.*` (API billing errors + billing UI, WP-114),
 * `team.*` / `roles.*` / `invites.*` (WP-112 team, roles & invites: API errors and dashboard
 * copy) and `email.*` (transactional email copy).
 */
export type MessageKey = Leaves<typeof en>;

export const catalogs: Readonly<Record<Locale, Catalog>> = { en, ar };

/** Flattens a nested catalog into `dotted.key → message` pairs. */
export function flattenCatalog(catalog: Catalog, prefix = ""): Map<string, string> {
  const out = new Map<string, string>();
  for (const [segment, value] of Object.entries(catalog)) {
    const key = prefix === "" ? segment : `${prefix}.${segment}`;
    if (typeof value === "string") out.set(key, value);
    else for (const [k, v] of flattenCatalog(value, key)) out.set(k, v);
  }
  return out;
}
